<template>
    <div class="bg-[color:var(--background)]">
        <!-- <h1 class="text-6xl">### {{ hero_index }} ###</h1> -->

        <Hero :title="heroTitle" :highlight-words="heroKeywords" :buttons="[{
            link: 'https://app.unified.to',
            title: 'Start free 30-day trial',
        },
        {
            link: 'https://calendly.com/d/cph9-g8n-jzg/connect-with-unified',
            title: 'Book a demo',
            icon: 'ic:round-keyboard-arrow-right',
        }]" :subtitle="heroSubtitle" :footer-items="TRIAL_POINTS" :is-invisible="true" />

        <HeroImage image_url="/images/Hero-Graphic2.webp" :image_width="2282" :image_height="1246"
            image_srcset="/images/Hero-Graphic2-320w.webp 320w, /images/Hero-Graphic2-480w.webp 480w, /images/Hero-Graphic2-640w.webp 640w, /images/Hero-Graphic2-960w.webp 960w, /images/Hero-Graphic2-1280w.webp 1280w, /images/Hero-Graphic2-1600w.webp 1600w, /images/Hero-Graphic2.webp 2282w"
            image_sizes="calc(85vw - 40px)" />

        <section class="mt-20 mb-20">
            <Customers lazy />
        </section>

        <FirstApiCall />

        <Modern />

        <Benefits label bottom_image lazy_images title="Deploy to production in days" :benefits="[
            {
                image_url: '/images/Step-1-Activate.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-1-Activate'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Activate integrations',
                body: `Enable integrations across ${Object.keys(MAP_CATS).length} API categories, from CRM to Accounting and Messaging.`,
            },
            {
                image_url: '/images/Step-2-Launch.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-2-Launch'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Launch integrations',
                body: 'Write one integration per category against a single data model, test it on real accounts during your free trial, then switch on every integration in that category.',
            },
            {
                image_url: '/images/Step-3-Authorize.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-3-Authorize'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Authorize integrations',
                body: auth_body,
            },
        ]" />

        <ApiVsMcp />


        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Fresh data to fuel countless AI use cases"
                subtitle="Power AI copilots, agents, insights, predictive analysis, and automation with a modern integration infrastructure that scales alongside you."
:usecases="USE_CASES" />
        </section>

        <IntegrationsHero />

        <QuadLayout title="The AI-first unified API platform"
            subtitle="Unified maintains every integration, data model, authorization flow and webhook, so your team builds against one API and never maintains connectors."
            :platformfeatures="[
                {
                    image_url: '/images/SDKs.svg',
                    title: 'Modern SDKs',
                    body: 'Typed SDKs for TypeScript, Python, Go and more, updated automatically with every API release.',
                    rectangular: false,
                },
                {
                    image_url: '/images/Complete-Unification.svg',
                    title: 'Complete unification',
                    body: `We've unified everything: API endpoints, data models, authorization, webhooks and permission scopes for seamless builds and launches.`,
                    rectangular: true,
                },
                {
                    image_url: '/images/Real-time-Data.svg',
                    title: 'Native webhooks',
                    body: 'When an integration sends its own webhooks, Unified delivers them to you through one consistent webhook API, in the same unified data model as every other call.',
                    rectangular: true,
                },
                {
                    image_url: '/images/Virtual-Webhooks.svg',
                    title: 'Virtual webhooks',
                    body: `We offer a single consistent webhook API with a sophisticated scheduling architecture for integrations that don't support them natively, giving you greater control over API requests and spend.`,
                    rectangular: false,
                },
            ]" />

        <Benefits bottom_image title="Front-end components for you and your users" :benefits="[
            {
                image_url: '/images/Embedded-Authorization.svg',
                image_width: 713,
                image_height: 572,
                title: 'Authorization',
                body: authorization_body,
            },
            {
                image_url: '/images/Embedded-Sign-up.svg',
                image_width: 772,
                image_height: 908,
                title: 'Single Sign-On',
                body: 'Add user logins and signups with OAuth 2 OpenID and 60+ identity providers.',
            },
            {
                image_url: '/images/Embedded-Directory.svg',
                image_width: 772,
                image_height: 908,
                title: 'Directory',
                body: 'Add active integrations to a web or app page and update it without code changes.',
            },
        ]" />

        <Testimonials :featured="['Oz L.', 'Mike K.', 'Lucian S.']" />

        <Hero title="Integrate. Faster." heading-level="h2" colour-index="1" bg :footer-items="TRIAL_POINTS" :buttons="[{
            link: 'https://app.unified.to',
            title: 'Start free 30-day trial',
        },
        {
            link: '/contact',
            title: 'Speak to an expert',
            icon: 'ic:round-keyboard-arrow-right',
        }]" />
    </div>
</template>

<script lang="ts">
import { INTEGRATIONS, INTEGRATIONS_COUNT_10s } from '~~/services/integrations';
import { MAP_CATS } from '~~/services/categories';

// Responsive variants for the three "Deploy to production" step images (see
// TICKET-10191). The rendered slot is 256px at >=640px (capped by the
// sm:max-w-xs card minus its p-8 padding) and ~100vw-64px below that, so
// mobile — not desktop — determines the largest candidate needed.
const STEP_IMAGE_WIDTHS = [254, 508, 644, 966];
function STEP_IMAGE_SRCSET(base: string): string {
    return STEP_IMAGE_WIDTHS.map((w) => `/images/${base}-${w}w.webp ${w}w`).join(', ');
}
const STEP_IMAGE_SIZES = '(min-width: 640px) 256px, calc(100vw - 64px)';

// Shown under the hero CTAs: prospects often don't realise the trial needs no
// card and works with real customer accounts (Test plan: unlimited calls and
// connections in non-production apps).
const TRIAL_POINTS = ['Free for 30 days', 'No credit card required', 'Integrate with real data'];

// One card per category so the breadth reads at a glance; each links to its category page.
const USE_CASES = [
    { title: 'Generate leads', body: 'Push qualified leads into your customers\' CRMs and keep contacts and deals up to date.', category: 'CRM', link: '/crm', linkTitle: 'CRM integrations' },
    { title: 'Source candidates', body: 'Pull jobs and push candidates and assessment results back to your customers\' ATS.', category: 'ATS', link: '/ats', linkTitle: 'ATS integrations' },
    { title: 'Onboard employees', body: 'Onboard employees and update profiles when roles change.', category: 'HRIS', link: '/hris', linkTitle: 'HRIS integrations' },
    { title: 'Analyze financials', body: 'Pull ledgers, invoices and transactions to power insights and ROI calculations.', category: 'ACCOUNTING', link: '/accounting', linkTitle: 'Accounting integrations' },
    { title: 'Automate inventory', body: 'Spot stock shortages and trigger refills across your customers\' stores.', category: 'COMMERCE', link: '/commerce', linkTitle: 'Commerce integrations' },
    { title: 'Automate legal billing', body: 'Capture billable time and expenses against matters in law-firm practice management systems.', category: 'LEGAL', link: '/legal', linkTitle: 'Legal integrations' },
    { title: 'Compare GPU clouds', body: 'Show GPU prices and availability across providers and launch instances on the cheapest one.', category: 'COMPUTE', link: '/compute', linkTitle: 'Compute integrations' },
    { title: 'Book patient appointments', body: 'Show open slots and book appointments directly in clinic and health system EHRs.', category: 'HEALTHCARE', link: '/healthcare', linkTitle: 'Healthcare integrations' },
].map(({ link, linkTitle, ...u }) => ({ ...u, button: { link, title: linkTitle, icon: 'ic:round-keyboard-arrow-right' } }));

export default {
    props: {
        dark: Boolean,
    },
    setup() {
        // A single headline. The previous A/B pick ran in useState during static
        // generation, so every visitor saw the same variant until the next deploy.
        const heroTitle = 'Secure real-time unified API+MCP';
        const heroKeywords = ['unified', 'API+MCP'];

        const heroSubtitle = `Unlock your customers' data from ${INTEGRATIONS.length} sources across ${Object.keys(MAP_CATS).length} categories — built for B2B SaaS applications and AI agents using a real-time unified API + MCP.`;

        useSEO({
            title: 'Unified — Real-time Unified API and MCP Platform',
            noSuffix: true,
            description:
                'Developers who use our Unified API launch integrations in hours instead of months. One API and data model to add, update, and monitor HR, sales, marketing, support, ticketing and accounting integrations. Start with a free 30-day trial, no credit card required.',
        });

        // Point LLMs and AI crawlers arriving on the home page to the site's
        // Markdown resources: llms.txt indexes every page that has a clean
        // Markdown (`.md`) version, and llms-full.txt is the full-text corpus.
        // The home page's own `.md` sibling (/index.md, generated from the
        // integrations dataset) is advertised separately via PageActions.
        useHead({
            link: [
                { rel: 'alternate', type: 'text/markdown', href: 'https://unified.to/llms.txt', title: 'llms.txt' },
                { rel: 'alternate', type: 'text/markdown', href: 'https://unified.to/llms-full.txt', title: 'llms-full.txt' },
            ],
        });

        return {
            MAP_CATS,
            INTEGRATIONS,
            heroKeywords,
            heroTitle,
            heroSubtitle,
            INTEGRATIONS_COUNT_10s,
            STEP_IMAGE_SRCSET,
            STEP_IMAGE_SIZES,
            TRIAL_POINTS,
            USE_CASES,
        }
    },
    data() {
        return {
            authorization_body: 'Allow users to easily link their accounts with a single authorization flow. <br/><a href="/embeddedauth" class="text-blue-500 underline">Learn more about Embedded Auth</a>.',
            auth_body: 'Embed our authorization component to let customers easily link their accounts. <br/><a href="/embeddedauth" class="text-blue-500 underline">Learn more about Embedded Auth</a>.',
        };
    },
    methods: {},
    mounted() {
        // console.log('mounted', this.hero_index);
        // this.HERO = HEROS[this.hero_index];
    },
    // watch: {
    //     hero_index() {
    //         console.log('hero index changed', this.hero_index);
    //     },
    // },
};
</script>